import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Plus, Quote, Trash2, Type, X } from 'lucide-react';
import type { StickyAudioMapEntry } from '../../utils/stickyAudio';
import { isAnchorOutsideView } from '../../utils/textAudioTipPlayback';
import { leitnerService, LeitnerSourceKind, emitLeitnerChanged } from '../../services/leitnerService';
import { WORD_POS_I18N } from '../../utils/wordPartOfSpeech';
import { WordPartOfSpeech } from '../../services/contentService';
import { useAppLocale } from '../../providers/LocaleProvider';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { VocabWordImage } from '../shared/VocabCollapseMedia';
import { VocabPronounceButton } from '../shared/VocabPronounceButton';
import { LEARNER_UI_PERSIAN_STACK } from '../../constants/learnerPersianFonts';
import { useNotifications } from '../feedback/NotificationSystem';

interface WordHelpAnchorRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

interface WordHelpCardProps {
  entry: StickyAudioMapEntry;
  rect: WordHelpAnchorRect;
  anchor?: HTMLElement;
  lessonId?: number;
  libraryBookId?: number;
  /** Admin preview: show lookup UI without writing to a Leitner box. */
  readOnly?: boolean;
  onClose: () => void;
}

const WORD_HELP_ANIM_MS = 200;
const CONFIRM_MS = 220;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const WordHelpCard: React.FC<WordHelpCardProps> = ({
  entry,
  rect: initialRect,
  anchor,
  lessonId,
  libraryBookId,
  readOnly = false,
  onClose
}) => {
  const { t } = useTranslation('wordHelp');
  const { t: tLeitner } = useTranslation('leitner');
  const { showSuccess, showError } = useNotifications();
  const { locale, direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [inBox, setInBox] = useState<boolean | null>(null);
  const [cardId, setCardId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmClosing, setConfirmClosing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(initialRect);
  const closingRef = useRef(false);
  const pos =
    WORD_POS_I18N[(entry.partOfSpeech ?? WordPartOfSpeech.Other) as WordPartOfSpeech] ??
    WORD_POS_I18N[WordPartOfSpeech.Other];
  const canAdd = Boolean(entry.english?.trim()) && inBox === false;
  const isConfirmOpen = confirmOpen || confirmClosing;
  const posLabel = locale === 'fa' ? pos.fa : pos.en;

  useLayoutEffect(() => {
    if (prefersReducedMotion()) {
      setVisible(true);
      return;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (prefersReducedMotion()) {
      onClose();
      return;
    }
    setVisible(false);
  };

  const dismissConfirm = useCallback(() => {
    if (confirmClosing) return;
    setConfirmClosing(true);
    window.setTimeout(() => {
      setConfirmOpen(false);
      setConfirmClosing(false);
    }, CONFIRM_MS);
  }, [confirmClosing]);

  useEffect(() => {
    if (visible || !closingRef.current) return;
    const timeoutId = window.setTimeout(onClose, WORD_HELP_ANIM_MS + 40);
    return () => window.clearTimeout(timeoutId);
  }, [visible, onClose]);

  useEffect(() => {
    if (readOnly) {
      setInBox(null);
      setCardId(null);
      return undefined;
    }
    let cancelled = false;
    void leitnerService
      .status([entry.phrase])
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((item) => item.persian === entry.phrase) ?? rows[0];
        setInBox(Boolean(row?.inBox));
        setCardId(row?.cardId ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setInBox(false);
          setCardId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [entry.phrase, readOnly]);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const root = document.querySelector('[data-word-help-confirm]');
      if (root?.contains(event.target as Node)) return;
      dismissConfirm();
    };
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [confirmOpen, dismissConfirm]);

  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-word-help-card], [data-learner-fullscreen-image]')) return;
      requestClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (document.querySelector('[data-learner-fullscreen-image]')) return;
        requestClose();
      }
    };
    const followAnchor = (event: Event) => {
      const card = document.querySelector('[data-word-help-card]');
      if (card && event.target instanceof Node && card.contains(event.target)) return;
      const el = anchor;
      if (!el) return;
      if (isAnchorOutsideView(el)) {
        requestClose();
        return;
      }
      const next = el.getBoundingClientRect();
      setRect({
        top: next.top,
        left: next.left,
        bottom: next.bottom,
        right: next.right,
        width: next.width,
        height: next.height
      });
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', followAnchor, true);
    window.addEventListener('resize', followAnchor);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', followAnchor, true);
      window.removeEventListener('resize', followAnchor);
    };
  }, [onClose, anchor]);

  const add = async () => {
    if (readOnly || !canAdd || busy) return;
    setBusy(true);
    try {
      const result = await leitnerService.bulkAdd([
        {
          persian: entry.phrase,
          english: entry.english ?? '',
          phonetic: entry.phonetic,
          partOfSpeech: entry.partOfSpeech,
          example: entry.example,
          imageUrl: entry.imageUrl,
          audioUrl: entry.audioUrl || undefined,
          audioFileName: entry.audioFileName,
          sourceKind: libraryBookId ? LeitnerSourceKind.Book : LeitnerSourceKind.Lesson,
          sourceLessonId: lessonId,
          sourceLibraryBookId: libraryBookId,
          sourceEntryKey: entry.entryKey
        }
      ]);
      const nextId = result.cards?.[0]?.id;
      if (nextId) setCardId(nextId);
      setInBox(true);
      setConfirmOpen(false);
      setConfirmClosing(false);
      if (result.added > 0) {
        showSuccess(tLeitner('addedToBox', { count: result.added }));
        emitLeitnerChanged({ added: true });
      } else {
        emitLeitnerChanged();
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : tLeitner('addFailed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (readOnly || !cardId || busy) return;
    setBusy(true);
    try {
      await leitnerService.remove(cardId);
      setInBox(false);
      setCardId(null);
      setConfirmOpen(false);
      setConfirmClosing(false);
      emitLeitnerChanged();
      showSuccess(tLeitner('removedFromBox'));
    } catch (err) {
      showError(err instanceof Error ? err.message : tLeitner('deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  const left = Math.min(Math.max(8, rect.left), window.innerWidth - 300);
  const top = rect.bottom + 8 + 220 > window.innerHeight ? Math.max(8, rect.top - 8) : rect.bottom + 8;
  const placeAbove = top < rect.top;

  return (
    <div
      data-word-help-card
      role="dialog"
      aria-label={entry.phrase}
      dir={isRtl ? 'rtl' : 'ltr'}
      className={`learner-word-help-panel fixed z-[80] w-[18rem] rounded-lg border border-gray-400/35 bg-white/90 px-3 py-2 shadow-lg backdrop-blur-[2px] dark:border-gray-500/35 dark:bg-gray-950/85${
        visible ? ' is-visible' : ''
      }`}
      style={{
        left,
        top: placeAbove ? undefined : top,
        bottom: placeAbove ? window.innerHeight - rect.top + 8 : undefined,
        fontFamily: LEARNER_UI_PERSIAN_STACK
      }}
      onClick={() => {
        if (isConfirmOpen) {
          dismissConfirm();
          return;
        }
        setExpanded((open) => !open);
      }}
      onTransitionEnd={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.propertyName !== 'opacity') return;
        if (!visible && closingRef.current) onClose();
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-1.5">
          <VocabPronounceButton
            url={entry.audioUrl}
            ariaLabel={t('play')}
            size="md"
            className="mt-0.5"
          />
          <div className="min-w-0">
            <div dir="rtl" className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {entry.phrase}
            </div>
            {entry.english ? (
              <div dir="ltr" className="text-xs text-gray-600 dark:text-gray-300">
                {entry.english}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!readOnly && (inBox || canAdd) ? (
            <div className="relative flex items-center">
              {isConfirmOpen ? (
                <div
                  data-word-help-confirm
                  className={`absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-2 rounded-lg border border-gray-400/35 bg-white/20 px-3 py-0.5 shadow-lg backdrop-blur-[2px] dark:border-gray-500/35 dark:bg-gray-950/20 ${
                    confirmClosing ? 'animate-favorite-confirm-out' : 'animate-favorite-confirm-in'
                  } ${isRtl ? 'left-full ml-1.5' : 'right-full mr-1.5'}`}
                  onClick={(event) => event.stopPropagation()}
                  role="group"
                  aria-label={inBox ? tLeitner('confirmRemoveFromBox') : tLeitner('confirmAdd')}
                >
                  <span className="whitespace-nowrap text-xs font-semibold text-gray-800 drop-shadow-sm dark:text-gray-100">
                    {inBox ? tLeitner('confirmRemoveFromBox') : tLeitner('confirmAdd')}
                  </span>
                  <button
                    type="button"
                    disabled={busy || confirmClosing}
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissConfirm();
                    }}
                    className="min-w-[2.25rem] rounded-md px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-black/10 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-white/10"
                  >
                    {tLeitner('confirmRemoveNo')}
                  </button>
                  <button
                    type="button"
                    disabled={busy || confirmClosing}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (inBox) void remove();
                      else void add();
                    }}
                    className="min-w-[2.25rem] rounded-md bg-rose-500/25 px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-500/35 disabled:opacity-50 dark:text-rose-200 dark:hover:bg-rose-500/30"
                  >
                    {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden /> : tLeitner('confirmRemoveYes')}
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                disabled={busy || isConfirmOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  if (isConfirmOpen) return;
                  setConfirmClosing(false);
                  setConfirmOpen(true);
                }}
                className="relative z-20 shrink-0 rounded-lg p-1 text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-100 dark:text-rose-300 dark:hover:bg-rose-950/50"
                title={inBox ? tLeitner('delete') : tLeitner('addChip')}
                aria-label={inBox ? tLeitner('delete') : tLeitner('addChip')}
              >
                {busy && !isConfirmOpen ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : inBox ? (
                  <Trash2 className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </button>
            </div>
          ) : null}
          <ChevronDown
            className={`h-3.5 w-3.5 text-rose-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
          <button
            type="button"
            className="rounded p-0.5 text-gray-500 hover:bg-black/10 dark:text-gray-300 dark:hover:bg-white/10"
            aria-label={t('close')}
            onClick={(event) => {
              event.stopPropagation();
              requestClose();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <CollapsibleFadePanel expanded={expanded}>
          <div className="mt-2 flex items-start gap-2 border-t border-gray-200/80 pt-2 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 text-start">
              <span className="inline-flex w-max rounded-full border border-gray-200 px-1.5 py-px text-[10px] font-medium leading-4 text-gray-600 dark:border-gray-600 dark:text-gray-300">
                {posLabel}
              </span>
              {entry.phonetic ? (
                <div dir="ltr" className="flex w-max items-center gap-1.5 text-sky-700 dark:text-sky-300">
                  <Type className="h-3 w-3 shrink-0" aria-hidden />
                  {entry.phonetic}
                </div>
              ) : null}
              {entry.example ? (
                <div className="flex w-full gap-1.5">
                  <Quote className="mt-0.5 h-3 w-3 shrink-0 text-rose-300" aria-hidden />
                  <p dir={isRtl ? 'rtl' : 'ltr'} className="min-w-0 whitespace-pre-wrap text-start">
                    {entry.example}
                  </p>
                </div>
              ) : null}
            </div>
            {entry.imageUrl ? <VocabWordImage url={entry.imageUrl} /> : null}
          </div>
      </CollapsibleFadePanel>
    </div>
  );
};
